import {
  decodeServerMessage,
  encodeClientMessage,
  identityIdFromSigningKey,
  nextBackoff,
  signValue,
  type ChannelProof,
  type IdentityMaterial,
  type RelayServerIdentity,
  type PushWake,
  type RecipientStatus,
  type PushWakeRevocation,
  type WebPushSubscription,
  type SyncEnvelope,
} from "@palestra/core";
import {
  acknowledge,
  queueRecipientStatus,
  pendingRecipientStatuses,
  updateRecipientStatus,
  enqueue,
  getMeta,
  markAttempt,
  markEnvelopeHandled,
  queuedItems,
  setMeta,
  wasEnvelopeHandled,
} from "./storage";

/**
 * Un envelope che l'applicazione rifiuta e rifiutera' sempre: autore che non
 * corrisponde al mittente, firma non valida, ruolo senza titolo, payload che non
 * passa lo schema. Riceverlo di nuovo non cambierebbe nulla, quindi va
 * confermato al relay comunque — altrimenti resta in giacenza e torna a ogni
 * riconnessione per trenta giorni, bloccando la sincronizzazione di chi lo
 * riceve. E' il contrario di un guasto locale, che invece va ritentato.
 */
export class RejectedEnvelopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RejectedEnvelopeError";
  }
}

/**
 * Quanto indietro guarda il cursore rispetto all'ultimo contatto. Il `since`
 * dell'`hello` confronta l'istante dichiarato da chi ha *creato* l'envelope con
 * quello letto dal relay: due orologi diversi. Un giorno di margine rende
 * l'errore di orologio irrilevante e toglie comunque dalla riconsegna le
 * settimane precedenti.
 */
const CURSOR_SKEW_MS = 24 * 60 * 60 * 1000;

const CURSOR_KEY = "relayCursor";

/**
 * Quale relay ha risposto l'ultima volta. Sta su disco e non solo in memoria:
 * la domanda «con chi parla questo telefono» ha una risposta anche prima del
 * primo contatto della sessione — e soprattutto quando il contatto non riesce,
 * che e' il momento in cui la si fa davvero.
 */
const RELAY_SERVER_KEY = "relayServer";

export type SyncState =
  "offline" | "unreachable" | "connecting" | "unavailable" | "syncing" | "synced" | "error";

export interface SyncSnapshot {
  state: SyncState;
  pending: number;
  lastContact?: string;
  message?: string;
  activity?: "sending" | "confirmation" | "waiting";
  waitReason?: "traffic" | "retry" | "recipients";
  retryAt?: string;
  /**
   * Chi c'e' dall'altra parte. Il valore corrente è `go`; `js` resta accettato
   * per non invalidare metadati scritti da installazioni precedenti.
   */
  relay?: RelayServerIdentity;
}

/** I tempi sono iniettabili perche' i test non possono attendere i valori reali. */
export interface SyncTimings {
  /** Tetto sul singolo tentativo: un host che scarta i pacchetti non fallisce da solo. */
  connectTimeoutMs: number;
  /** Permanenza minima di "connecting": senza, un fallimento immediato non e' percepibile. */
  connectingFloorMs: number;
  /** Attesa dopo l'ultima conferma prima di riallineare il contatore alla coda. */
  reconcileDelayMs: number;
  /** Silenzio massimo tollerato prima di verificare che il canale sia vivo. */
  heartbeatIntervalMs: number;
  /** Attesa della risposta alla verifica prima di dichiarare morto il canale. */
  heartbeatTimeoutMs: number;
  /** Budget condiviso da publish, ACK e messaggi di servizio. */
  rateWindowMs: number;
  publishIntervalMs: number;
  publishTimeoutMs: number;
  quotaRetryBaseMs: number;
  quotaRetryMaxMs: number;
}

export const defaultSyncTimings: SyncTimings = {
  connectTimeoutMs: 7000,
  connectingFloorMs: 2000,
  reconcileDelayMs: 250,
  heartbeatIntervalMs: 25_000,
  heartbeatTimeoutMs: 10_000,
  rateWindowMs: 60_000,
  publishIntervalMs: 700,
  publishTimeoutMs: 10_000,
  quotaRetryBaseMs: 5000,
  quotaRetryMaxMs: 60_000,
};

/**
 * Con l'app sotto gli occhi dell'utente un'attesa da un minuto e' indistinguibile
 * da un guasto; nascosta, non la vede nessuno e conviene risparmiare tentativi.
 */
const FOREGROUND_BACKOFF_CAP_MS = 15_000;

interface RelaySyncOptions {
  url: string;
  /**
   * La casella sul relay. Non si deriva piu' dal `gymId`: il canale di una
   * palestra e' ancorato alla chiave di chi l'ha fondata, e quello del
   * ripristino e' la casella personale dell'identita'.
   */
  channelId: string;
  /**
   * Il titolo per starci. Assente per la casella personale, che il relay
   * ricalcola da solo; per il canale di una palestra e' cio' che distingue un
   * membro da chiunque ne conosca il nome.
   */
  channelProof?: ChannelProof;
  identity: IdentityMaterial;
  /**
   * Distingue le due caselle di consegna della stessa persona. Senza, la
   * conferma di lettura del telefono cancellerebbe dal relay quello che il
   * computer non ha ancora ricevuto.
   */
  deviceId?: string;
  onEnvelope: (envelope: SyncEnvelope) => Promise<void>;
  onState: (snapshot: SyncSnapshot) => void;
  /** Canale autenticato: il momento in cui vale la pena chiedere allineamento. */
  onAuthenticated?: (serverTime: string) => void;
  onPushConfig?: (publicKey: string | undefined) => void;
  onPublished?: (envelopeId: string) => Promise<void> | void;
  timings?: Partial<SyncTimings>;
}

export class RelaySyncClient {
  readonly #options: RelaySyncOptions;
  readonly #timings: SyncTimings;
  #socket: WebSocket | undefined;
  #authenticated = false;
  #retryTimer: number | undefined;
  #connectTimer: number | undefined;
  #floorTimer: number | undefined;
  #reconcileTimer: number | undefined;
  #heartbeatTimer: number | undefined;
  #heartbeatDeadline: number | undefined;
  // 90 messaggi/finestra contro i 120 del relay; 10 posti riservati ai
  // controlli, così un recupero massivo non impedisce ping, backup e inviti.
  #sentAt: number[] = [];
  readonly #acks = new Set<string>();
  #workTimer: number | undefined;
  #workAt = 0;
  #flushing = false;
  #flushAgain = false;
  #inFlight: { id: string; until: number } | undefined;
  #nextPublishAt = 0;
  #quotaUntil = 0;
  #quotaFailures = 0;
  #attempts = 0;
  #closed = false;
  /** Falso solo dopo un rifiuto del canale: allora non si riprova piu'. */
  #admitted = true;
  /** Verita' per la UI: un contatore, non una rilettura. Vedi design.md, decisione 1. */
  #pending = 0;
  #state: SyncState;
  #message: string | undefined;
  #progress: Pick<SyncSnapshot, "activity" | "waitReason" | "retryAt"> = {};
  /** L'errore riportato dal relay sopravvive alla chiusura del socket che lo segue. */
  #stickyError: string | undefined;
  /** Solo la sessione corrente: "mai parlato col relay da quando ho aperto l'app". */
  #lastContact: string | undefined;
  readonly #backupWaiters = new Set<(blob: string | null) => void>();
  /**
   * Le conferme dei depositi di link, per token: un `invite.put` non e'
   * riuscito finche' il relay non risponde `invite.stored`, perche' un link
   * consegnato senza la cassetta dietro aprirebbe un 410 al primo tocco.
   */
  readonly #inviteWaiters = new Map<
    string,
    { resolve: () => void; fail: (reason: Error) => void }
  >();
  #floorUntil = 0;
  /**
   * Fin dove si e' gia' guardato. Senza, ogni riconnessione ripartiva
   * dall'inizio della giacenza — fino a trenta giorni di envelope da
   * riesaminare a ogni cambio di rete.
   */
  #cursor: string | undefined;
  /** L'ultimo relay che si e' dichiarato, ricordato fra un avvio e l'altro. */
  #relayServer: RelayServerIdentity | undefined;

  constructor(options: RelaySyncOptions) {
    this.#options = options;
    this.#timings = { ...defaultSyncTimings, ...options.timings };
    // Come per il cursore: la lettura dal disco non deve poter sovrascrivere una
    // dichiarazione piu' fresca arrivata nel frattempo.
    void getMeta<RelayServerIdentity>(RELAY_SERVER_KEY)
      .then((server) => {
        if (!server || this.#relayServer) return;
        this.#relayServer = server;
        this.#emit();
      })
      .catch(() => undefined);
    // La lettura dal disco arriva dopo: se nel frattempo un contatto ha gia'
    // portato un cursore piu' recente, quello vince — altrimenti il valore
    // vecchio cancellerebbe il nuovo.
    void getMeta<string>(CURSOR_KEY)
      .then((cursor) => {
        if (cursor && !this.#cursor) this.#cursor = cursor;
      })
      .catch(() => undefined);
    // "Sincronizzato" significa aver parlato con il relay, non che il browser
    // creda di avere rete: prima del primo contatto l'unica verita' e' l'attesa.
    this.#state = navigator.onLine ? "connecting" : "offline";
    void this.#reconcile();
  }

  connect(): void {
    if (this.#closed) return;
    if (!navigator.onLine) {
      this.#publishState("offline");
      return;
    }
    this.#open(true);
  }

  /**
   * Tentativo richiesto dall'utente: ignora navigator.onLine, che mente dietro
   * i captive portal e non cambia mai quando e' il relay a tornare disponibile.
   */
  reconnectNow(): void {
    if (this.#closed) return;
    if (this.#socket?.readyState === WebSocket.CONNECTING) return;
    // Un tentativo chiesto dall'utente rimette in gioco anche il canale
    // rifiutato: nel frattempo puo' essere arrivato un lasciapassare nuovo.
    this.#admitted = true;
    this.#attempts = 0;
    if (this.#retryTimer) window.clearTimeout(this.#retryTimer);
    this.#retryTimer = undefined;
    if (!this.#quotaFailures) this.#stickyError = undefined;
    if (this.#socket?.readyState === WebSocket.OPEN) {
      if (this.#authenticated) this.#ping();
      void this.#flush().catch(() => undefined);
      return;
    }
    this.#open(true);
  }

  /**
   * Ritorno in primo piano. Un socket puo' sopravvivere formalmente allo standby
   * senza trasportare piu' nulla: qui si verifica, non si presume. Se il canale
   * risponde non viene annunciato niente, altrimenti ogni cambio di scheda
   * farebbe lampeggiare un indicatore di riconnessione che non serve a nessuno.
   */
  ensureAlive(): void {
    if (this.#closed) return;
    const socket = this.#socket;
    // Un tentativo in corso ha gia' il suo tetto temporale: non se ne affianca un altro.
    if (socket?.readyState === WebSocket.CONNECTING) return;
    if (socket?.readyState === WebSocket.OPEN) {
      if (this.#authenticated) this.#ping();
      return;
    }
    this.reconnectNow();
  }

  /**
   * Senza conferma dedicata: il backup e' idempotente e il `put` successivo
   * sovrascrive comunque. Falso se il canale non era autenticato, cosi' il
   * chiamante sa di dover riprovare al prossimo `ready`.
   */
  putBackup(blob: string): boolean {
    if (!this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) return false;
    return this.#send(encodeClientMessage({ type: "backup.put", blob }));
  }

  /**
   * Cancella la copia di recupero custodita dal relay. Attende la conferma —
   * un `backup` vuoto — perche' chi chiama sta per cancellare anche il
   * dispositivo: dirgli che e' andata quando non e' andata significherebbe
   * lasciare in giro l'unica copia rimasta senza che nessuno lo sappia.
   */
  deleteBackup(timeoutMs = this.#timings.connectTimeoutMs): Promise<void> {
    if (!this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("Canale relay non autenticato"));
    }
    return new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.#backupWaiters.delete(settle);
        reject(new Error("Il relay non ha risposto alla cancellazione del backup"));
      }, timeoutMs);
      const settle = () => {
        window.clearTimeout(timer);
        resolve();
      };
      this.#backupWaiters.add(settle);
      if (!this.#send(encodeClientMessage({ type: "backup.delete" }))) {
        window.clearTimeout(timer);
        this.#backupWaiters.delete(settle);
        reject(new Error("Sincronizzazione occupata: riprova tra poco"));
      }
    });
  }

  /**
   * Deposita il blob cifrato di un link di invito e attende la conferma del
   * relay. Rifiuta subito se il canale non e' autenticato: chi chiama deve
   * poter dire all'utente che serve la connessione, non lasciarlo aspettare.
   */
  putInviteLink(
    input: { token: string; blob: string; expiresAt: string },
    timeoutMs = this.#timings.connectTimeoutMs,
  ): Promise<void> {
    if (!this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("Canale relay non autenticato"));
    }
    return new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.#inviteWaiters.delete(input.token);
        reject(new Error("Il relay non ha confermato il link di invito"));
      }, timeoutMs);
      this.#inviteWaiters.set(input.token, {
        resolve: () => {
          window.clearTimeout(timer);
          resolve();
        },
        fail: (reason) => {
          window.clearTimeout(timer);
          reject(reason);
        },
      });
      if (!this.#send(encodeClientMessage({ type: "invite.put", ...input }))) {
        window.clearTimeout(timer);
        this.#inviteWaiters.delete(input.token);
        reject(new Error("Sincronizzazione occupata: riprova tra poco"));
      }
    });
  }

  /**
   * Il ritiro del link e' senza conferma, come il `put` del backup: e'
   * idempotente, e la revoca vera — `spentInvites` — e' gia' partita comunque.
   * Falso se il canale non era autenticato, cosi' chi chiama sa che il link
   * potrebbe restare aperto fino alla scadenza.
   */
  deleteInviteLink(token: string): boolean {
    if (!this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) return false;
    return this.#send(encodeClientMessage({ type: "invite.delete", token }));
  }

  /** Attende la risposta: `null` significa "nessun backup", non un errore. */
  getBackup(timeoutMs = this.#timings.connectTimeoutMs): Promise<string | null> {
    if (!this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("Canale relay non autenticato"));
    }
    return new Promise<string | null>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.#backupWaiters.delete(settle);
        reject(new Error("Il relay non ha risposto alla richiesta di backup"));
      }, timeoutMs);
      const settle = (blob: string | null) => {
        window.clearTimeout(timer);
        resolve(blob);
      };
      this.#backupWaiters.add(settle);
      if (!this.#send(encodeClientMessage({ type: "backup.get" }))) {
        window.clearTimeout(timer);
        this.#backupWaiters.delete(settle);
        reject(new Error("Sincronizzazione occupata: riprova tra poco"));
      }
    });
  }

  /**
   * `dedupeKey` dichiara che questo envelope *sostituisce* quello identico gia'
   * in attesa invece di affiancarglisi. Vale per le richieste idempotenti, non
   * per le modifiche di dominio: vedi `SyncQueueItem.dedupeKey`.
   */
  async publish(
    envelope: SyncEnvelope,
    dedupeKey?: string,
    wake?: PushWake,
    deliveryMode?: "transient",
    replacementKey?: string,
  ): Promise<void> {
    const removed = await enqueue({
      envelope,
      attempts: 0,
      nextAttemptAt: new Date().toISOString(),
      ...(dedupeKey ? { dedupeKey } : {}),
      ...(wake ? { wake } : {}),
      ...(deliveryMode ? { deliveryMode } : {}),
      ...(replacementKey ? { replacement: { key: replacementKey, version: 1 } } : {}),
    });
    // Uno in piu' meno quelli che ha preso il posto di: un incremento secco
    // conterebbe come nuovi anche i duplicati appena rimossi.
    this.#pending = Math.max(0, this.#pending + 1 - removed);
    this.#publishState(this.#state, this.#message);
    this.#scheduleReconcile();
    await this.#flush();
  }

  subscribePush(subscription: WebPushSubscription): boolean {
    if (!this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) return false;
    return this.#send(encodeClientMessage({ type: "push.subscribe", subscription }));
  }

  unsubscribePush(): boolean {
    if (!this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) return false;
    return this.#send(encodeClientMessage({ type: "push.unsubscribe" }));
  }

  revokePushGrant(revocation: PushWakeRevocation): boolean {
    if (!this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) return false;
    return this.#send(encodeClientMessage({ type: "push.grant.revoke", revocation }));
  }

  async setRecipientStatus(status: RecipientStatus): Promise<void> {
    if (
      status.channelId !== this.#options.channelId ||
      status.issuerSigningKey !== this.#options.identity.signingPublicKey
    )
      throw new Error("Decisione sui destinatari fuori dal profilo corrente");
    await queueRecipientStatus(status);
    this.#scheduleWork(0);
  }

  /** Spedisce ciò che è già stato inserito da una transazione di dominio. */
  async flushQueued(): Promise<void> {
    await this.#flush();
  }

  close(): void {
    this.#closed = true;
    this.#stopWork();
    this.#stopHeartbeat();
    if (this.#retryTimer) window.clearTimeout(this.#retryTimer);
    if (this.#connectTimer) window.clearTimeout(this.#connectTimer);
    if (this.#floorTimer) window.clearTimeout(this.#floorTimer);
    if (this.#reconcileTimer) window.clearTimeout(this.#reconcileTimer);
    if (this.#socket?.readyState === WebSocket.OPEN) this.#socket.close();
  }

  /**
   * `announce` distingue il tentativo voluto dall'utente dal ritentativo di
   * fondo: annunciare anche il secondo farebbe lampeggiare "Riprovo…" a ogni
   * ciclo di backoff, senza mai lasciar vedere l'esito.
   */
  #open(announce: boolean): void {
    if (
      this.#socket?.readyState === WebSocket.CONNECTING ||
      this.#socket?.readyState === WebSocket.OPEN
    ) {
      return;
    }
    if (this.#retryTimer) window.clearTimeout(this.#retryTimer);
    this.#retryTimer = undefined;
    if (this.#connectTimer) window.clearTimeout(this.#connectTimer);
    this.#authenticated = false;
    this.#stopWork();
    // La quota di traffico del relay è per connessione. La pausa di deposito
    // invece sopravvive alla riconnessione e ai tentativi manuali.
    this.#sentAt = [];
    // I timer del socket precedente non hanno piu' un canale da sorvegliare.
    this.#stopHeartbeat();
    if (announce && !this.#quotaFailures) this.#publishState("connecting");
    const socket = new WebSocket(this.#options.url);
    this.#socket = socket;
    this.#connectTimer = window.setTimeout(() => {
      if (socket !== this.#socket || this.#authenticated) return;
      this.#retire(socket);
    }, this.#timings.connectTimeoutMs);
    socket.addEventListener("open", () => {
      if (this.#closed || socket !== this.#socket) {
        socket.close();
        return;
      }
      this.#send(
        encodeClientMessage({
          type: "hello",
          channelId: this.#options.channelId,
          clientId: this.#options.identity.id,
          signingPublicKey: this.#options.identity.signingPublicKey,
          ...(this.#options.deviceId ? { deviceId: this.#options.deviceId } : {}),
          ...(this.#since() ? { since: this.#since() } : {}),
        }),
      );
    });
    socket.addEventListener("message", (event) => {
      if (this.#closed || socket !== this.#socket) return;
      void this.#handleMessage(String(event.data), socket).catch((error: unknown) => {
        if (this.#closed || socket !== this.#socket) return;
        const message = error instanceof Error ? error.message : "Errore di sincronizzazione";
        this.#stickyError = message;
        this.#publishState("error", message);
      });
    });
    // L'esito del tentativo si decide qui e solo qui: il listener "error" non
    // pubblica nulla, altrimenti il "close" che lo segue ne cancella il messaggio.
    socket.addEventListener("close", () => {
      if (socket !== this.#socket) return;
      if (this.#connectTimer) window.clearTimeout(this.#connectTimer);
      this.#connectTimer = undefined;
      this.#scheduleReconnect();
    });
  }

  /** Il browser può restare in CLOSING dopo standby: non aspettare il suo close. */
  #retire(socket: WebSocket): void {
    if (this.#closed || socket !== this.#socket) return;
    this.#socket = undefined;
    if (this.#connectTimer) window.clearTimeout(this.#connectTimer);
    this.#connectTimer = undefined;
    socket.close();
    this.#scheduleReconnect();
  }

  async #handleMessage(raw: string, socket: WebSocket): Promise<void> {
    const message = decodeServerMessage(raw);
    this.#noteAlive();
    if (message.type === "pong") {
      // La prova di vitalita' e' gia' stata registrata da #noteAlive: qui non
      // c'e' nessuno stato da annunciare.
      return;
    }
    if (message.type === "backup") {
      for (const waiter of [...this.#backupWaiters]) waiter(message.blob);
      this.#backupWaiters.clear();
      return;
    }
    if (message.type === "invite.stored") {
      this.#inviteWaiters.get(message.token)?.resolve();
      this.#inviteWaiters.delete(message.token);
      return;
    }
    if (message.type === "challenge") {
      const proof = this.#options.channelProof;
      const signed = {
        challenge: message.challenge,
        channelId: this.#options.channelId,
        clientId: this.#options.identity.id,
        // Firmare anche il lasciapassare lo lega a questa challenge: staccarlo
        // e riattaccarlo altrove invaliderebbe la firma.
        ...(proof?.length ? { channelProof: proof } : {}),
      };
      this.#send(
        encodeClientMessage({
          type: "authenticate",
          ...signed,
          signature: signValue(signed, this.#options.identity.signingPrivateKey),
        }),
      );
      return;
    }
    if (message.type === "ready") {
      this.#authenticated = true;
      this.#attempts = 0;
      if (!this.#quotaFailures) this.#stickyError = undefined;
      if (this.#connectTimer) window.clearTimeout(this.#connectTimer);
      this.#connectTimer = undefined;
      this.#scheduleHeartbeat();
      await this.#noteRelayServer(message.server);
      // Il relay ha gia' deciso che cosa consegnare con il cursore precedente:
      // avanzarlo adesso vale per la prossima riconnessione, non per questa.
      await this.#advanceCursor(message.serverTime);
      await this.#noteContact(message.serverTime);
      if (this.#closed || socket !== this.#socket) return;
      this.#options.onPushConfig?.(message.pushPublicKey);
      await this.#flush();
      if (!this.#closed && socket === this.#socket)
        this.#options.onAuthenticated?.(message.serverTime);
      return;
    }
    if (message.type === "recipient.status.stored") {
      await updateRecipientStatus(message.status, true);
      if (!this.#closed && socket === this.#socket) this.#scheduleWork(0);
      return;
    }
    if (message.type === "published") {
      this.#progress = {};
      // Decremento sincrono prima di toccare il disco: l'ordine di completamento
      // delle transazioni non puo' piu' riordinare gli snapshot.
      this.#pending = Math.max(0, this.#pending - 1);
      // Solo l'esito del tentativo corrente sblocca una pausa di quota.
      const current = this.#inFlight?.id === message.envelopeId;
      if (current) {
        this.#quotaFailures = 0;
        this.#quotaUntil = 0;
      }
      if (!this.#quotaFailures) this.#stickyError = undefined;
      this.#publishState(this.#pending > 0 ? "syncing" : "synced");
      this.#scheduleReconcile();
      await this.#options.onPublished?.(message.envelopeId);
      await acknowledge(message.envelopeId);
      if (this.#closed || socket !== this.#socket) return;
      if (current && this.#inFlight?.id === message.envelopeId) this.#inFlight = undefined;
      this.#scheduleWork(0);
      return;
    }
    if (
      message.type === "push.subscribed" ||
      message.type === "push.unsubscribed" ||
      message.type === "push.grant.revoked"
    ) {
      return;
    }
    if (message.type === "envelope") {
      /**
       * Ricezione e accettazione sono due cose diverse, e confonderle costava
       * la sincronizzazione: la conferma partiva solo dopo un'elaborazione
       * riuscita, quindi un envelope che l'applicazione rifiuta restava in
       * giacenza e tornava a ogni riconnessione, riportando ogni volta il
       * client in errore. Qui si conferma tutto cio' che e' stato *ricevuto* e
       * si registra cio' che e' stato *visto*; e' l'autorizzazione di dominio a
       * decidere se applicarlo, e quella decisione non torna indietro.
       */
      const envelopeId = message.envelope.id;
      if (await wasEnvelopeHandled(envelopeId)) {
        // Gia' visto: la conferma precedente non e' arrivata al relay, non c'e'
        // nulla da rifare se non ridirglielo.
        if (!this.#closed && socket === this.#socket) this.#ack(envelopeId);
        return;
      }
      try {
        await this.#options.onEnvelope(message.envelope);
      } catch (error) {
        // Un guasto locale — spazio esaurito, database chiuso — non e' un
        // rifiuto: non si conferma, cosi' il relay lo ripropone.
        if (!(error instanceof RejectedEnvelopeError)) throw error;
      }
      await markEnvelopeHandled(envelopeId);
      if (this.#closed || socket !== this.#socket) return;
      this.#ack(envelopeId);
      await this.#noteContact(new Date().toISOString());
      this.#publishState(this.#pending > 0 ? "syncing" : "synced");
      return;
    }
    if (message.type === "error") {
      /**
       * Il canale rifiutato non e' un guasto: e' una risposta definitiva. Il
       * lasciapassare e' scaduto o non c'e' mai stato, e nessun tentativo lo
       * fara' comparire — riprovare in eterno mostrerebbe "riprovo" per un
       * problema che si risolve solo con un invito nuovo.
       */
      if (message.code === "channel-forbidden") {
        this.#admitted = false;
        this.#stickyError = undefined;
        this.#publishState("unavailable", "Serve un nuovo invito per questa palestra");
        this.#socket?.close();
        return;
      }
      /**
       * Un rifiuto del deposito appartiene a chi lo sta aspettando, non al
       * badge di sincronizzazione: il canale funziona, e' quel link a non
       * essere stato accolto. Il relay non dice per quale token, ma un solo
       * deposito alla volta e' il caso reale.
       */
      if (message.code.startsWith("invite-")) {
        for (const [token, waiter] of [...this.#inviteWaiters]) {
          this.#inviteWaiters.delete(token);
          waiter.fail(new Error(message.message));
        }
        return;
      }
      if (message.code === "sender-quota" || message.code === "store-capacity") {
        this.#quotaFailures += 1;
        const delay = Math.min(
          this.#timings.quotaRetryMaxMs,
          this.#timings.quotaRetryBaseMs * 2 ** Math.min(this.#quotaFailures - 1, 16),
        );
        this.#quotaUntil = Date.now() + delay;
        this.#progress = {
          activity: "waiting",
          waitReason: "retry",
          retryAt: new Date(this.#quotaUntil).toISOString(),
        };
        this.#inFlight = undefined;
        this.#scheduleWork(delay);
      }
      this.#stickyError = message.message;
      this.#publishState("error", message.message);
    }
  }

  async #flush(): Promise<void> {
    if (this.#flushing) {
      this.#flushAgain = true;
      return;
    }
    const socket = this.#socket;
    if (this.#closed || !this.#authenticated || socket?.readyState !== WebSocket.OPEN) return;
    this.#flushing = true;
    try {
      // I controlli firmati precedono gli ACK e non dipendono dalla quota envelope.
      const statuses = await pendingRecipientStatuses(this.#options.channelId);
      if (this.#closed || socket !== this.#socket || socket.readyState !== WebSocket.OPEN) return;
      const status = statuses.find((entry) => entry.nextAttemptAt <= Date.now());
      if (status && this.#budgetAt(90) <= Date.now()) {
        await updateRecipientStatus(status.status, false);
        if (this.#closed || socket !== this.#socket || socket.readyState !== WebSocket.OPEN) return;
        this.#send(encodeClientMessage({ type: "recipient.status", status: status.status }));
      }
      if (statuses.length)
        this.#scheduleWork(
          Math.max(
            1000,
            this.#budgetAt(90) - Date.now(),
            Math.min(...statuses.map((entry) => entry.nextAttemptAt)) - Date.now(),
          ),
        );
      this.#drainAcks();
      const items = await queuedItems();
      if (this.#closed || socket !== this.#socket || socket.readyState !== WebSocket.OPEN) return;
      this.#pending = items.length + statuses.length;
      // La riammissione deve essere confermata prima delle nuove risposte:
      // altrimenti il relay potrebbe scartarle mentre il destinatario è revocato.
      if (!items.length || statuses.length) {
        this.#progress = statuses.length ? { activity: "waiting", waitReason: "recipients" } : {};
        this.#publishState(this.#pending ? "syncing" : "synced");
        return;
      }
      const now = Date.now();
      const next = Math.max(
        this.#quotaUntil,
        this.#nextPublishAt,
        this.#inFlight?.until ?? 0,
        this.#budgetAt(80),
        Math.min(...items.map((item) => Date.parse(item.nextAttemptAt))),
      );
      if (next > now) {
        this.#progress =
          this.#inFlight && this.#inFlight.until > now
            ? { activity: "confirmation" }
            : next - now < 1000 && next === this.#nextPublishAt
              ? { activity: "sending" }
              : {
                  activity: "waiting",
                  waitReason: this.#budgetAt(80) > now ? "traffic" : "retry",
                  retryAt: new Date(next).toISOString(),
                };
        this.#publishState("syncing");
        this.#scheduleWork(next - now);
        return;
      }
      this.#inFlight = undefined;
      const item = items.find((entry) => Date.parse(entry.nextAttemptAt) <= now)!;
      // La transazione ricontrolla scadenza e presenza: un ACK o un'altra
      // scheda possono aver consumato l'item dopo la lettura della coda.
      if (!(await markAttempt(item.envelope.id, now))) {
        this.#scheduleWork(0);
        return;
      }
      if (this.#closed || socket !== this.#socket || socket.readyState !== WebSocket.OPEN) return;
      if (this.#quotaUntil > Date.now() || this.#budgetAt(80) > Date.now()) {
        this.#scheduleWork(0);
        return;
      }
      this.#inFlight = { id: item.envelope.id, until: Date.now() + this.#timings.publishTimeoutMs };
      this.#nextPublishAt = Date.now() + this.#timings.publishIntervalMs;
      this.#progress = { activity: "confirmation" };
      this.#publishState("syncing");
      this.#write(
        encodeClientMessage({
          type: "publish",
          envelope: item.envelope,
          ...(item.wake ? { wake: item.wake } : {}),
          ...(item.deliveryMode ? { deliveryMode: item.deliveryMode } : {}),
          ...(item.replacement ? { replacement: item.replacement } : {}),
        }),
      );
      this.#scheduleWork(this.#timings.publishTimeoutMs);
    } finally {
      this.#flushing = false;
      if (this.#flushAgain) {
        this.#flushAgain = false;
        this.#scheduleWork(0);
      }
    }
  }

  #scheduleWork(delay: number): void {
    if (this.#closed || this.#socket?.readyState !== WebSocket.OPEN) return;
    const at = Date.now() + Math.max(0, delay);
    if (this.#workTimer !== undefined && this.#workAt <= at) return;
    if (this.#workTimer !== undefined) window.clearTimeout(this.#workTimer);
    this.#workAt = at;
    this.#workTimer = window.setTimeout(
      () => {
        this.#workTimer = undefined;
        this.#drainAcks();
        void this.#flush().catch((error: unknown) => {
          if (this.#closed) return;
          this.#stickyError = error instanceof Error ? error.message : "Errore di sincronizzazione";
          this.#publishState("error", this.#stickyError);
          this.#scheduleWork(this.#timings.publishTimeoutMs);
        });
      },
      Math.max(0, at - Date.now()),
    );
  }

  #stopWork(): void {
    if (this.#workTimer !== undefined) window.clearTimeout(this.#workTimer);
    this.#workTimer = undefined;
    this.#acks.clear();
    this.#inFlight = undefined;
    this.#progress = {};
  }

  #budgetAt(limit: number): number {
    const now = Date.now();
    this.#sentAt = this.#sentAt.filter((at) => at + this.#timings.rateWindowMs >= now);
    return this.#sentAt.length < limit
      ? now
      : this.#sentAt[this.#sentAt.length - limit]! + this.#timings.rateWindowMs + 1;
  }

  #ack(envelopeId: string): void {
    this.#acks.add(envelopeId);
    this.#drainAcks();
  }

  #drainAcks(): void {
    if (this.#closed || !this.#authenticated || this.#socket?.readyState !== WebSocket.OPEN) return;
    for (const envelopeId of this.#acks) {
      const wait = this.#budgetAt(80) - Date.now();
      if (wait > 0) {
        this.#scheduleWork(wait);
        break;
      }
      this.#write(encodeClientMessage({ type: "ack", envelopeId }));
      this.#acks.delete(envelopeId);
    }
  }

  #scheduleReconnect(): void {
    if (this.#closed) return;
    this.#stopWork();
    this.#authenticated = false;
    this.#stopHeartbeat();
    // Il canale ci ha detto di no: la riconnessione automatica qui e' solo
    // rumore. Resta possibile un tentativo voluto dall'utente.
    if (!this.#admitted) {
      this.#publishState("unavailable", "Serve un nuovo invito per questa palestra");
      return;
    }
    this.#attempts += 1;
    // Nascosta, l'app tiene il tetto pieno deciso da nextBackoff.
    const capMs = document.visibilityState === "visible" ? FOREGROUND_BACKOFF_CAP_MS : undefined;
    const when = Date.parse(nextBackoff(this.#attempts, Date.now(), capMs));
    this.#retryTimer = window.setTimeout(
      () => {
        this.#retryTimer = undefined;
        if (this.#closed) return;
        if (!navigator.onLine) {
          this.#publishState("offline");
          return;
        }
        this.#open(false);
      },
      Math.max(0, when - Date.now()),
    );
    if (this.#stickyError) {
      this.#publishState("error", this.#stickyError);
      return;
    }
    this.#publishState(navigator.onLine ? "unreachable" : "offline");
  }

  /**
   * Il ping del protocollo WebSocket non arriva all'applicazione: il browser
   * risponde da solo e non espone nulla. Senza un messaggio applicativo, un
   * socket rimasto OPEN dopo lo standby resta indistinguibile da uno vivo e la
   * UI continua a dichiarare "sincronizzato" mentre il canale non porta piu' nulla.
   */
  #scheduleHeartbeat(): void {
    if (this.#heartbeatTimer) window.clearTimeout(this.#heartbeatTimer);
    this.#heartbeatTimer = window.setTimeout(() => {
      this.#heartbeatTimer = undefined;
      if (this.#closed || !this.#authenticated) return;
      this.#ping();
    }, this.#timings.heartbeatIntervalMs);
  }

  #ping(): void {
    const socket = this.#socket;
    if (socket?.readyState !== WebSocket.OPEN) return;
    if (this.#heartbeatDeadline !== undefined) return;
    if (!this.#send(encodeClientMessage({ type: "ping", sentAt: new Date().toISOString() }))) {
      this.#scheduleHeartbeat();
      return;
    }
    if (this.#heartbeatDeadline) window.clearTimeout(this.#heartbeatDeadline);
    this.#heartbeatDeadline = window.setTimeout(() => {
      this.#heartbeatDeadline = undefined;
      if (this.#closed || socket !== this.#socket) return;
      this.#stopHeartbeat();
      this.#retire(socket);
    }, this.#timings.heartbeatTimeoutMs);
  }

  /**
   * Qualsiasi messaggio del relay prova che il canale e' vivo, anche un errore:
   * si pinga solo nel silenzio, mai su un canale che sta gia' lavorando.
   */
  #noteAlive(): void {
    if (this.#heartbeatDeadline) window.clearTimeout(this.#heartbeatDeadline);
    this.#heartbeatDeadline = undefined;
    if (this.#authenticated) this.#scheduleHeartbeat();
  }

  #stopHeartbeat(): void {
    if (this.#heartbeatTimer) window.clearTimeout(this.#heartbeatTimer);
    if (this.#heartbeatDeadline) window.clearTimeout(this.#heartbeatDeadline);
    this.#heartbeatTimer = undefined;
    this.#heartbeatDeadline = undefined;
  }

  #scheduleReconcile(): void {
    if (this.#reconcileTimer) window.clearTimeout(this.#reconcileTimer);
    this.#reconcileTimer = window.setTimeout(() => {
      this.#reconcileTimer = undefined;
      void this.#reconcile();
    }, this.#timings.reconcileDelayMs);
  }

  /**
   * Ultima parola sul conteggio: copre le altre schede, il riaccodamento dello
   * stesso envelope e l'avvio a freddo, che il solo contatore in memoria sbaglia.
   */
  async #reconcile(): Promise<void> {
    if (this.#closed) return;
    let items;
    let statuses;
    try {
      items = await queuedItems();
      if (this.#closed) return;
      statuses = await pendingRecipientStatuses(this.#options.channelId);
    } catch {
      // Lo storage locale puo' essere chiuso o esaurito: il conteggio in memoria
      // resta l'ultima approssimazione valida, non un motivo per fallire.
      return;
    }
    if (this.#closed) return;
    this.#pending = items.length + statuses.length;
    if (this.#pending === 0 && this.#state === "syncing") {
      this.#publishState("synced");
      return;
    }
    this.#publishState(this.#state, this.#message);
  }

  /**
   * Il momento del contatto vale per la UI quanto per il disco: senza tenerlo
   * anche in memoria, la causa di un errore arriva senza il "quando" che la
   * rende leggibile.
   */
  /** Il cursore da mandare: l'ultimo contatto, con il margine per gli orologi. */
  #since(): string | undefined {
    if (!this.#cursor) return undefined;
    const contact = Date.parse(this.#cursor);
    if (Number.isNaN(contact)) return undefined;
    return new Date(contact - CURSOR_SKEW_MS).toISOString();
  }

  async #advanceCursor(instant: string): Promise<void> {
    // Mai all'indietro: un relay riavviato con l'orologio storto non deve far
    // riesaminare settimane di giacenza.
    if (this.#cursor && Date.parse(instant) <= Date.parse(this.#cursor)) return;
    this.#cursor = instant;
    await setMeta(CURSOR_KEY, instant);
  }

  async #noteContact(instant: string): Promise<void> {
    this.#lastContact = instant;
    await setMeta("lastContact", instant);
  }

  /**
   * Un relay che non si dichiara non cancella quello che si era dichiarato
   * prima: dire «non so con chi parlo» dopo aver parlato con qualcuno sarebbe
   * meno vero, non piu' prudente.
   */
  async #noteRelayServer(server: RelayServerIdentity | undefined): Promise<void> {
    if (!server) return;
    const known = this.#relayServer;
    this.#relayServer = server;
    if (known?.kind === server.kind && known.version === server.version) return;
    await setMeta(RELAY_SERVER_KEY, server);
  }

  #send(message: string): boolean {
    if (
      this.#closed ||
      this.#socket?.readyState !== WebSocket.OPEN ||
      this.#budgetAt(90) > Date.now()
    )
      return false;
    this.#write(message);
    return true;
  }

  #write(message: string): void {
    this.#sentAt.push(Date.now());
    this.#socket!.send(message);
  }

  #publishState(state: SyncState, message?: string): void {
    // Un errore riportato dal relay non viene cancellato da un esito ottimista
    // partito prima: solo una conferma o un nuovo tentativo lo revocano.
    if (this.#stickyError && (state === "syncing" || state === "synced")) {
      this.#emit();
      return;
    }
    this.#state = state;
    this.#message = message;
    if (state === "connecting") {
      this.#floorUntil = Date.now() + this.#timings.connectingFloorMs;
      if (this.#floorTimer) window.clearTimeout(this.#floorTimer);
      this.#floorTimer = undefined;
      this.#emit();
      return;
    }
    // Il floor trattiene la presentazione dell'esito, mai la connessione, e solo
    // quando l'esito riporta al punto di partenza: un successo dimostra da se'
    // che il tocco ha funzionato e non ha motivo di attendere.
    const setback = state === "unreachable" || state === "offline" || state === "error";
    const remaining = setback ? this.#floorUntil - Date.now() : 0;
    if (remaining > 0) {
      if (this.#floorTimer) window.clearTimeout(this.#floorTimer);
      this.#floorTimer = window.setTimeout(() => {
        this.#floorTimer = undefined;
        this.#floorUntil = 0;
        this.#emit();
      }, remaining);
      return;
    }
    if (this.#floorTimer) window.clearTimeout(this.#floorTimer);
    this.#floorTimer = undefined;
    this.#floorUntil = 0;
    this.#emit();
  }

  #emit(): void {
    this.#options.onState({
      state: this.#state,
      pending: this.#pending,
      ...(this.#lastContact ? { lastContact: this.#lastContact } : {}),
      ...(this.#message ? { message: this.#message } : {}),
      ...(this.#relayServer ? { relay: this.#relayServer } : {}),
      ...(this.#pending > 0 && this.#authenticated ? this.#progress : {}),
    });
  }
}

export function validateRelayIdentity(clientId: string, signingPublicKey: string): boolean {
  return identityIdFromSigningKey(signingPublicKey) === clientId;
}
